// Bidirectional delta sync: pull cloud changes for a plan and warm the local cache,
// so other devices' changes are available even after going offline.
import axios from "axios";
import { API } from "@/lib/api";
import { idb } from "@/lib/offlineDb";

const raw = axios.create({ baseURL: API, withCredentials: true });
const running = new Set(); // per plan: parallel prefetch of several plans must not skip each other

function upsert(arr, recs) {
  const map = new Map((arr || []).map((x) => [x.id, x]));
  recs.forEach((r) => map.set(r.id, r));
  return [...map.values()];
}
async function mergeCache(key, recs) {
  if (!recs || !recs.length) return;
  const c = await idb.get("cache", key);
  await idb.put("cache", { key, data: upsert(c ? c.data : [], recs), ts: Date.now() });
}
function groupBy(recs, f) {
  const g = {};
  (recs || []).forEach((r) => { const k = f(r); if (k) (g[k] = g[k] || []).push(r); });
  return g;
}

export async function pullDelta(planId) {
  if (!planId || !navigator.onLine || running.has(planId)) return;
  running.add(planId);
  try {
    const cursorKey = `__cursor:/plans/${planId}`;
    const cur = await idb.get("cache", cursorKey);
    const since = cur ? cur.since : null;
    const { data } = await raw.get("/sync/pull", { params: { plan_id: planId, since } });
    await mergeCache(`/plans/${planId}/positions`, data.positions);
    for (const [pid, recs] of Object.entries(groupBy(data.tickets, (r) => r.position_id))) await mergeCache(`/positions/${pid}/tickets`, recs);
    for (const [pid, recs] of Object.entries(groupBy(data.defects, (r) => r.position_id))) await mergeCache(`/positions/${pid}/defects`, recs);
    for (const [pid, recs] of Object.entries(groupBy(data.comments, (r) => r.position_id))) await mergeCache(`/positions/${pid}/comments`, recs);
    for (const [pid, recs] of Object.entries(groupBy(data.quantities, (r) => r.position_id))) await mergeCache(`/positions/${pid}/quantities`, recs);
    for (const [did, recs] of Object.entries(groupBy((data.files || []).filter((f) => f.defect_id), (r) => r.defect_id))) await mergeCache(`/defects/${did}/photos`, recs);
    for (const [pid, recs] of Object.entries(groupBy((data.files || []).filter((f) => !f.defect_id && f.position_id), (r) => r.position_id))) await mergeCache(`/positions/${pid}/files`, recs);
    await idb.put("cache", { key: cursorKey, since: data.now, ts: Date.now() });
  } catch (_) {} finally {
    running.delete(planId);
  }
}
